import React, { useState, useMemo, useCallback } from "react";
import { IUserReport } from "../../../services/types/users.types";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from "./selectContactPopup.module.css";

interface SelectContactPopupProps {
  contacts: IUserReport[];
  onConfirm: (contacts: IUserReport[]) => void;
  onClose: () => void;
  selectedUserIds?: number[];
  onClear?: () => void;
  showClearButton?: boolean;
  /**
   * Restricts the picker to one contact. Used where the caller can only store a single
   * recipient — without it the list invites a multi-selection the caller then ignores.
   */
  singleSelect?: boolean;
}

const SelectContactPopup: React.FC<SelectContactPopupProps> = ({
  contacts,
  onConfirm,
  onClose,
  selectedUserIds = [],
  onClear,
  showClearButton = false,
  singleSelect = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [checkedIds, setCheckedIds] = useState<Set<number>>(
    () => new Set(selectedUserIds)
  );

  const filteredContacts = useMemo(() => {
    if (!searchValue.trim()) {
      return contacts;
    }
    const searchLower = searchValue.toLowerCase().trim();
    return contacts.filter((contact) => {
      const user = contact.Users;
      const fullName = user.FullName?.toLowerCase() || "";
      const firstName = user.FirstName?.toLowerCase() || "";
      const lastName = user.LastName?.toLowerCase() || "";
      const phone = user.Phone?.toLowerCase() || "";
      const email = user.Email?.toLowerCase() || "";
      const id = user.Id?.toString() || "";

      return (
        fullName.includes(searchLower) ||
        firstName.includes(searchLower) ||
        lastName.includes(searchLower) ||
        phone.includes(searchLower) ||
        email.includes(searchLower) ||
        id.includes(searchLower)
      );
    });
  }, [contacts, searchValue]);

  const allFilteredChecked = useMemo(
    () =>
      filteredContacts.length > 0 &&
      filteredContacts.every((c) => checkedIds.has(c.Users.Id)),
    [filteredContacts, checkedIds]
  );

  const toggleOne = useCallback(
    (id: number) => {
      setCheckedIds((prev) => {
        if (singleSelect) {
          return prev.has(id) ? new Set<number>() : new Set([id]);
        }
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [singleSelect]
  );

  const toggleAll = useCallback(() => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredChecked) {
        filteredContacts.forEach((c) => next.delete(c.Users.Id));
      } else {
        filteredContacts.forEach((c) => next.add(c.Users.Id));
      }
      return next;
    });
  }, [allFilteredChecked, filteredContacts]);

  const handleConfirm = useCallback(() => {
    const selected = contacts.filter((c) => checkedIds.has(c.Users.Id));
    onConfirm(selected);
    onClose();
  }, [contacts, checkedIds, onConfirm, onClose]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t("CHOOSE_EXISTING_USER")}</h2>
        {checkedIds.size > 0 && (
          <span className={styles.selectedCount}>
            {checkedIds.size} {t("SELECTED")}
          </span>
        )}
      </div>

      <div className={styles.content}>
        {showClearButton && checkedIds.size > 0 && onClear && (
          <div className={styles.clearContainer}>
            <button
              type="button"
              className={styles.clearBtn}
              onClick={() => {
                setCheckedIds(new Set());
                onClear();
                onClose();
              }}
            >
              {t("CLEAR_USER_SELECTION")}
            </button>
          </div>
        )}

        <div className={styles.searchContainer}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder={t("SEARCH_BY_NAME_PHONE_EMAIL")}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            autoFocus
          />
        </div>

        <div className={styles.contactList}>
          {filteredContacts.length > 0 ? (
            <ul className={styles.list}>
              <li className={`${styles.listRow} ${styles.listRowHeader}`}>
                <span className={styles.listCell}>
                  {!singleSelect && (
                    <input
                      type="checkbox"
                      checked={allFilteredChecked}
                      onChange={toggleAll}
                      className={styles.checkbox}
                    />
                  )}
                </span>
                <span className={styles.listCell}>{t("COLUMN_ID")}</span>
                <span className={styles.listCell}>{t("NAME_2")}</span>
                <span className={styles.listCell}>{t("PHONE")}</span>
                <span className={styles.listCell}>{t("EMAIL")}</span>
              </li>
              {filteredContacts.map((contact, index) => (
                <li
                  key={contact.Users.Id}
                  className={`${styles.listRow} ${
                    index % 2 === 0 ? styles.listRowColored : ""
                  } ${checkedIds.has(contact.Users.Id) ? styles.selected : ""}`}
                  onClick={() => toggleOne(contact.Users.Id)}
                >
                  <span className={styles.listCell}>
                    <input
                      type="checkbox"
                      checked={checkedIds.has(contact.Users.Id)}
                      onChange={() => toggleOne(contact.Users.Id)}
                      onClick={(e) => e.stopPropagation()}
                      className={styles.checkbox}
                    />
                  </span>
                  <span className={styles.listCell}>{contact.Users.Id}</span>
                  <span className={styles.listCell}>
                    {contact.Users.FullName ||
                      `${contact.Users.FirstName} ${contact.Users.LastName || ""}`}
                  </span>
                  <span className={styles.listCell}>
                    {contact.Users.Phone || "-"}
                  </span>
                  <span className={styles.listCell}>
                    {contact.Users.Email || "-"}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.noResults}>{t("NO_USERS_FOUND")}</p>
          )}
        </div>

        <div className={styles.footerActions}>
          <button
            type="button"
            className={styles.confirmBtn}
            onClick={handleConfirm}
          >
            {t("CONFIRM_SELECTION")} {checkedIds.size > 0 ? `(${checkedIds.size})` : ""}
          </button>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={onClose}
          >
            {t("CANCEL")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectContactPopup;
